import React, { useCallback, useRef } from 'react';
import styled from 'styled-components';
import { spacing } from '../tokens/spacing';
import { typography } from '../tokens/typography';
import { borders } from '../tokens/borders';
import { animations } from '../tokens/animations';

export interface TabItem {
  id: string;
  label: string;
}

const TabListWrapper = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.$vertical ? 'column' : 'row')};
  gap: 0;
  min-width: ${props => (props.$vertical ? '180px' : 'auto')};
  border-bottom: ${props => (props.$vertical ? 'none' : `${borders.width.base} solid ${props.theme.colors.border}`)};
  border-right: ${props => (props.$vertical ? `${borders.width.base} solid ${props.theme.colors.border}` : 'none')};
  overflow-x: ${props => (props.$vertical ? 'hidden' : 'auto')};
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 4px;
  }
`;

const TabButton = styled.button<{ $active: boolean; $vertical?: boolean }>`
  padding: ${spacing[4]} ${spacing[6]};
  border: none;
  border-bottom: ${props => (props.$vertical ? 'none' : `${borders.width.thick} solid ${props.$active ? props.theme.colors.primary : 'transparent'}`)};
  border-right: ${props => (props.$vertical ? `${borders.width.thick} solid ${props.$active ? props.theme.colors.primary : 'transparent'}` : 'none')};
  border-radius: ${props => (props.$vertical ? `${borders.radius.md} 0 0 ${borders.radius.md}` : '0')};
  background: ${props =>
    props.$active ? props.theme.colors.surfaceHover : 'transparent'};
  color: ${props =>
    props.$active ? props.theme.colors.primary : props.theme.colors.text};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  font-weight: ${props =>
    props.$active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  text-align: ${props => (props.$vertical ? 'left' : 'center')};
  transition: ${animations.transition(
    ['color', 'background-color', 'border-color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  margin-bottom: ${props => (props.$vertical ? spacing[1] : `-${borders.width.base}`)};
  &:hover {
    background: ${props => props.theme.colors.surfaceHover};
    color: ${props => props.theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    padding: ${spacing[3]} ${spacing[4]};
  }
`;

const TabPanelsWrapper = styled.div<{ $vertical?: boolean }>`
  background: ${props => props.theme.colors.surface};
  border: ${borders.width.base} solid ${props => props.theme.colors.border};
  border-top: none;
  border-left: ${props => (props.$vertical ? 'none' : undefined)};
  border-radius: ${props =>
    props.$vertical ? `0 ${borders.radius.lg} ${borders.radius.lg} 0` : `0 0 ${borders.radius.lg} ${borders.radius.lg}`};
  padding: ${spacing[6]};
  flex: 1;
  min-width: 0;
`;

const TabsOuter = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.$vertical ? 'row' : 'column')};
  align-items: stretch;
  gap: 0;
`;

export interface TabsProps {
  activeTab: string;
  onTabChange: (tabId: string) => void;
  tabs: TabItem[];
  /** When true, tab labels are stacked vertically (one under the other). */
  variant?: 'horizontal' | 'vertical';
  'aria-label'?: string;
  children: React.ReactNode;
  className?: string;
}

export interface TabPanelProps {
  tabId: string;
  children: React.ReactNode;
  className?: string;
}

export const TabPanel: React.FC<TabPanelProps> = ({ tabId, children }) => {
  return <>{children}</>;
};

export const Tabs: React.FC<TabsProps> = ({
  activeTab,
  onTabChange,
  tabs,
  variant = 'horizontal',
  'aria-label': ariaLabel = 'Tabs',
  children,
  className,
}) => {
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const isVertical = variant === 'vertical';

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      let newIndex = currentIndex;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        newIndex = Math.max(0, currentIndex - 1);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        newIndex = Math.min(tabs.length - 1, currentIndex + 1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        newIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        newIndex = tabs.length - 1;
      } else {
        return;
      }
      const nextTab = tabs[newIndex];
      if (nextTab) {
        onTabChange(nextTab.id);
        tabRefs.current.get(nextTab.id)?.focus();
      }
    },
    [tabs, onTabChange]
  );

  const selectedPanel = React.Children.toArray(children).find(
    (child): child is React.ReactElement<TabPanelProps> =>
      React.isValidElement(child) && child.type === TabPanel && (child.props as TabPanelProps).tabId === activeTab
  );
  const panelContent = selectedPanel ? selectedPanel.props.children : null;

  const panelId = `tabpanel-${activeTab}`;
  const tabIdPrefix = 'tab-';

  return (
    <TabsOuter className={className} $vertical={isVertical}>
      <TabListWrapper
        ref={tabListRef}
        role="tablist"
        aria-label={ariaLabel}
        $vertical={isVertical}
      >
        {tabs.map((tab, index) => (
          <TabButton
            key={tab.id}
            ref={el => {
              if (el) tabRefs.current.set(tab.id, el);
            }}
            type="button"
            role="tab"
            id={`${tabIdPrefix}${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            $active={activeTab === tab.id}
            $vertical={isVertical}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={e => handleKeyDown(e, index)}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabListWrapper>
      <TabPanelsWrapper
        role="tabpanel"
        id={panelId}
        aria-labelledby={`${tabIdPrefix}${activeTab}`}
        tabIndex={0}
        $vertical={isVertical}
      >
        {panelContent}
      </TabPanelsWrapper>
    </TabsOuter>
  );
};

export default Tabs;
